import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { STORAGE_URL } from '../../api/axios';
import { dt, DashLang } from '../../utils/dashboardTranslations';

export default function StoreSettings() {
  const { store, reloadStore, lang } = useOutletContext<{ store: any; reloadStore: () => void; lang: DashLang }>();
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const domainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (store) {
      setForm({
        name: store.name || '',
        name_ar: store.name_ar || '',
        description: store.description || '',
        description_ar: store.description_ar || '',
        phone: store.phone || '',
        email: store.email || '',
        whatsapp: store.whatsapp || '',
        address: store.address || '',
        city: store.city || '',
        country: store.country || '',
        currency: store.currency || 'KWD',
        currency_symbol: store.currency_symbol || 'KD',
        online_stock_mode: store.online_stock_mode || 'store',
        pos_tax_rate: store.pos_tax_rate || 0,
        pos_receipt_size: store.pos_receipt_size || '80mm',
        pos_receipt_footer: store.pos_receipt_footer || '',
        pos_receipt_footer_ar: store.pos_receipt_footer_ar || '',
        instagram: store.instagram || '',
        twitter: store.twitter || '',
        facebook: store.facebook || '',
        tiktok: store.tiktok || '',
        tawk_key: store.tawk_key || '',
        delivery_enabled: store.delivery_enabled ?? true,
        pickup_enabled: store.pickup_enabled ?? true,
        delivery_fee: store.delivery_fee || 0,
        minimum_order: store.minimum_order || 0,
        estimated_delivery_time: store.estimated_delivery_time || 30,
        is_open: store.is_open ?? true,
        announcement: store.announcement || '',
        announcement_ar: store.announcement_ar || '',
        meta_title: store.meta_title || '',
        meta_title_ar: store.meta_title_ar || '',
        meta_description: store.meta_description || '',
        meta_description_ar: store.meta_description_ar || '',
        custom_domain: store.custom_domain || '',
      });
    }
  }, [store]);

  // Scroll to domain section if ?section=domain, reload store first
  useEffect(() => {
    if (searchParams.get('section') === 'domain') {
      reloadStore();
      setTimeout(() => domainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500);
    }
  }, [searchParams]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/stores/${store.id}`, form);
      toast.success(dt('settings_saved', lang));
      reloadStore();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error(dt('file_too_large', lang).replace('{size}', '2MB'));
      e.target.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await api.post(`/stores/${store.id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(dt('logo_updated', lang));
      reloadStore();
    } catch (err: any) {
      const msg = err.response?.data?.errors?.logo?.[0] || err.response?.data?.message;
      toast.error(msg || dt('error_uploading_logo', lang));
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error(dt('file_too_large', lang).replace('{size}', '10MB'));
      e.target.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('cover_image', file);
    try {
      await api.post(`/stores/${store.id}/cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(dt('cover_updated', lang));
      reloadStore();
    } catch (err: any) {
      const msg = err.response?.data?.errors?.cover_image?.[0] || err.response?.data?.message;
      toast.error(msg || dt('error_uploading_cover', lang));
    }
  };

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1>{dt('store_settings', lang)}</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? dt('saving', lang) : dt('save_changes', lang)}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* General */}
        <div className="card">
          <div className="card-header"><h2>{dt('general_info', lang)}</h2></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{dt('store_name', lang)} (EN)</label>
              <input className="form-input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('store_name', lang)} (AR)</label>
              <input className="form-input" dir="rtl" value={form.name_ar || ''} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} placeholder="اسم المتجر بالعربي" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{dt('description', lang)} (EN)</label>
              <textarea className="form-input" rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('description', lang)} (AR)</label>
              <textarea className="form-input" rows={3} dir="rtl" value={form.description_ar || ''} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} placeholder="وصف المتجر بالعربي" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{dt('announcement_banner', lang)} (EN)</label>
              <input className="form-input" value={form.announcement || ''} onChange={(e) => setForm({ ...form, announcement: e.target.value })} placeholder="e.g. Free delivery on orders above 5 KD!" />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('announcement_banner', lang)} (AR)</label>
              <input className="form-input" dir="rtl" value={form.announcement_ar || ''} onChange={(e) => setForm({ ...form, announcement_ar: e.target.value })} placeholder="مثال: توصيل مجاني للطلبات فوق 5 دينار!" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_open} onChange={(e) => setForm({ ...form, is_open: e.target.checked })} />
              {dt('store_is_open', lang)}
            </label>
          </div>
        </div>

        {/* Branding */}
        <div className="card">
          <div className="card-header"><h2>{dt('branding', lang)}</h2></div>
          <div className="form-group">
            <label className="form-label">{dt('logo', lang)}</label>
            {store.logo && (
              <img src={`${STORAGE_URL}/${store.logo}`} alt="Logo" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 12, objectFit: 'contain', marginBottom: 8 }} />
            )}
            <input type="file" accept="image/*" onChange={handleLogoUpload} className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">{dt('cover_image', lang)}</label>
            {store.cover_image && (
              <img src={`${STORAGE_URL}/${store.cover_image}`} alt="Cover" style={{ width: '100%', height: 120, borderRadius: 12, objectFit: 'cover', marginBottom: 8 }} />
            )}
            <input type="file" accept="image/*" onChange={handleCoverUpload} className="form-input" />
          </div>
        </div>

        {/* Contact */}
        <div className="card">
          <div className="card-header"><h2>{dt('contact_info', lang)}</h2></div>
          <div className="form-group">
            <label className="form-label">{dt('phone', lang)}</label>
            <input className="form-input" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">{dt('email', lang)}</label>
            <input className="form-input" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">{dt('whatsapp', lang)}</label>
            <input className="form-input" value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">{dt('address', lang)}</label>
            <input className="form-input" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">{dt('city', lang)}</label>
              <input className="form-input" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('country', lang)}</label>
              <input className="form-input" value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
          </div>
        </div>

        {/* Social */}
        <div className="card">
          <div className="card-header"><h2>{dt('social_media', lang)}</h2></div>
          <div className="form-group">
            <label className="form-label">Instagram</label>
            <input className="form-input" value={form.instagram || ''} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@yourstore" />
          </div>
          <div className="form-group">
            <label className="form-label">Twitter / X</label>
            <input className="form-input" value={form.twitter || ''} onChange={(e) => setForm({ ...form, twitter: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Facebook</label>
            <input className="form-input" value={form.facebook || ''} onChange={(e) => setForm({ ...form, facebook: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">TikTok</label>
            <input className="form-input" value={form.tiktok || ''} onChange={(e) => setForm({ ...form, tiktok: e.target.value })} />
          </div>
        </div>

        {/* Integrations */}
        <div className="card">
          <div className="card-header">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/images/tawk-logo.png" alt="Tawk.to" style={{ height: 22 }} />
              {lang === 'ar' ? 'الدردشة المباشرة' : 'Live Chat'}
            </h2>
          </div>
          <div className="form-group">
            <label className="form-label">{lang === 'ar' ? 'مفتاح Tawk.to' : 'Tawk.to Key'}</label>
            <input className="form-input" dir="ltr" value={form.tawk_key || ''} onChange={(e) => setForm({ ...form, tawk_key: e.target.value })} placeholder="e.g. 5a624e11d7591465c706eb8d/1hrromtlh" />
            <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
              {lang === 'ar' ? 'أدخل مفتاح Tawk.to الخاص بك لتفعيل الدردشة المباشرة في متجرك' : 'Enter your Tawk.to key to enable live chat on your store'}
            </p>
          </div>
        </div>

        {/* Delivery & Pickup */}
        <div className="card">
          <div className="card-header"><h2>{dt('delivery_pickup', lang)}</h2></div>
          <div style={{ display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.delivery_enabled} onChange={(e) => setForm({ ...form, delivery_enabled: e.target.checked })} />
              {dt('delivery_enabled', lang)}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.pickup_enabled} onChange={(e) => setForm({ ...form, pickup_enabled: e.target.checked })} />
              {dt('pickup_enabled', lang)}
            </label>
          </div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
            {lang === 'ar' ? 'رسوم التوصيل والحد الأدنى ووقت التوصيل يتم ضبطها من صفحة مناطق التوصيل' : 'Delivery fee, minimum order & delivery time are configured per area in Delivery Areas'}
          </p>
        </div>

        {/* Currency & SEO */}
        <div className="card">
          <div className="card-header"><h2>{dt('currency_seo', lang)}</h2></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">{dt('currency_code', lang)}</label>
              <input className="form-input" value={form.currency || ''} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('currency_symbol', lang)}</label>
              <input className="form-input" value={form.currency_symbol || ''} onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{dt('meta_title', lang)} (EN)</label>
              <input className="form-input" value={form.meta_title || ''} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('meta_title', lang)} (AR)</label>
              <input className="form-input" dir="rtl" value={form.meta_title_ar || ''} onChange={(e) => setForm({ ...form, meta_title_ar: e.target.value })} placeholder="عنوان الصفحة بالعربي" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{dt('meta_description', lang)} (EN)</label>
              <textarea className="form-input" rows={3} value={form.meta_description || ''} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">{dt('meta_description', lang)} (AR)</label>
              <textarea className="form-input" rows={3} dir="rtl" value={form.meta_description_ar || ''} onChange={(e) => setForm({ ...form, meta_description_ar: e.target.value })} placeholder="وصف الصفحة بالعربي" />
            </div>
          </div>
        </div>

        {/* Inventory & POS */}
        <div className="card">
          <div className="card-header"><h2>{lang === 'ar' ? 'المخزون ونقطة البيع' : 'Inventory & Point of Sale'}</h2></div>
          <div className="form-group">
            <label className="form-label">{lang === 'ar' ? 'مخزون المتجر الإلكتروني' : 'Online store stock source'}</label>
            <select className="form-input" value={form.online_stock_mode || 'store'} onChange={(e) => setForm({ ...form, online_stock_mode: e.target.value })}>
              <option value="store">{lang === 'ar' ? 'مخزون المنتج الرئيسي' : 'Main product stock'}</option>
              <option value="all_branches">{lang === 'ar' ? 'مجموع مخزون جميع الفروع النشطة' : 'Combined stock from all active branches'}</option>
            </select>
            <p style={{ fontSize: 12, color: 'var(--gray-400)', margin: '6px 0 0' }}>
              {lang === 'ar' ? 'عند اختيار جميع الفروع، يوزّع الطلب الإلكتروني الكمية تلقائياً على مخزون الفروع.' : 'With all branches selected, online orders automatically allocate stock across branches.'}
            </p>
          </div>
          {store.pos_enabled && <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'نسبة ضريبة نقطة البيع (%)' : 'POS tax rate (%)'}</label>
                <input className="form-input" type="number" min="0" max="100" step="0.001" value={form.pos_tax_rate ?? 0} onChange={(e) => setForm({ ...form, pos_tax_rate: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{lang === 'ar' ? 'حجم الإيصال الافتراضي' : 'Default receipt size'}</label>
                <select className="form-input" value={form.pos_receipt_size || '80mm'} onChange={(e) => setForm({ ...form, pos_receipt_size: e.target.value })}>
                  <option value="58mm">58 mm</option><option value="80mm">80 mm</option><option value="a4">A4</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group"><label className="form-label">{lang === 'ar' ? 'تذييل الإيصال (إنجليزي)' : 'Receipt footer (English)'}</label><textarea className="form-input" rows={2} value={form.pos_receipt_footer || ''} onChange={(e) => setForm({ ...form, pos_receipt_footer: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">{lang === 'ar' ? 'تذييل الإيصال (عربي)' : 'Receipt footer (Arabic)'}</label><textarea className="form-input" dir="rtl" rows={2} value={form.pos_receipt_footer_ar || ''} onChange={(e) => setForm({ ...form, pos_receipt_footer_ar: e.target.value })} /></div>
            </div>
          </>}
        </div>
      </div>

      {/* Domain Management */}
      <div ref={domainRef}>
        <DomainSection store={store} lang={lang} form={form} setForm={setForm} />
      </div>
    </div>
  );
}

function DomainSection({ store, lang, form, setForm }: { store: any; lang: any; form: any; setForm: any }) {
  const [tab, setTab] = useState<'own' | 'register'>(store?.custom_domain ? 'own' : 'register');
  const [ownDomain, setOwnDomain] = useState(store?.custom_domain || '');
  const [savingOwn, setSavingOwn] = useState(false);
  const [searchDomain, setSearchDomain] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [checking, setChecking] = useState(false);
  const [registering, setRegistering] = useState<string | null>(null);
  const [registered, setRegistered] = useState<any>(null);
  const [pricingInfo, setPricingInfo] = useState<any>(null);

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const isPaidPlan = store?.plan === 'business' || store?.plan === 'enterprise';

  useEffect(() => {
    if (isPaidPlan) {
      api.get(`/stores/${store.id}/domain/pricing`).then(res => setPricingInfo(res.data)).catch(() => {});
    }
  }, [store?.id, isPaidPlan]);

  const saveOwnDomain = async () => {
    if (!ownDomain.trim()) return;
    setSavingOwn(true);
    try {
      const res = await api.post(`/stores/${store.id}/domain/save-own`, { domain: ownDomain.trim() });
      setForm({ ...form, custom_domain: ownDomain.trim() });
      toast.success(t('Custom domain saved. Add a CNAME record in your DNS.', 'تم حفظ النطاق المخصص. أضف سجل CNAME في إعدادات DNS.'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Error saving domain', 'خطأ في حفظ النطاق'));
    } finally { setSavingOwn(false); }
  };

  const removeOwnDomain = async () => {
    if (!window.confirm(t('Remove custom domain?', 'إزالة النطاق المخصص؟'))) return;
    setSavingOwn(true);
    try {
      await api.delete(`/stores/${store.id}/domain`);
      setOwnDomain('');
      setForm({ ...form, custom_domain: null });
      toast.success(t('Domain removed', 'تم إزالة النطاق'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Error', 'خطأ'));
    } finally { setSavingOwn(false); }
  };

  const checkDomain = async () => {
    if (!searchDomain.trim()) return;
    setChecking(true);
    setResults([]);
    setRegistered(null);
    try {
      const clean = searchDomain.replace(/\s/g, '').toLowerCase().replace(/\.[a-z]+$/i, '');
      const res = await api.post(`/stores/${store.id}/domain/check`, {
        domain: clean,
        tlds: ['com', 'net', 'org', 'info', 'biz', 'co', 'io', 'me', 'online', 'store', 'shop', 'site'],
      });
      setResults(res.data || []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Error checking domain', 'خطأ في البحث عن النطاق'));
    } finally { setChecking(false); }
  };

  const registerDomain = async (domain: string, useFreeCredit: boolean) => {
    const msg = useFreeCredit
      ? t(`Register ${domain} for free?`, `تسجيل ${domain} مجاناً؟`)
      : t(`Register ${domain}?`, `تسجيل ${domain}؟`);
    if (!window.confirm(msg)) return;
    setRegistering(domain);
    try {
      const res = await api.post(`/stores/${store.id}/domain/register`, { domain, years: 1, use_free_credit: useFreeCredit });
      setRegistered(res.data);
      setForm({ ...form, custom_domain: domain });
      toast.success(t('Domain registered!', 'تم تسجيل النطاق!'));
      if (useFreeCredit && pricingInfo) setPricingInfo({ ...pricingInfo, eligible_for_free: false, free_domain_used: domain });
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Registration failed', 'فشل التسجيل'));
    } finally { setRegistering(null); }
  };

  return (
    <>
      {/* Current Domain */}
      {store?.custom_domain && (
        <div className="card">
          <div className="card-header">
            <h2>{t('Active Domain', 'النطاق النشط')}</h2>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 18, fontWeight: 700, fontFamily: 'monospace', direction: 'ltr' }}>{store.custom_domain}</span>
              <button className="btn btn-sm btn-outline" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={removeOwnDomain} disabled={savingOwn}>
                {t('Remove', 'إزالة')}
              </button>
            </div>
            <div style={{ padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}>
              <strong>CNAME</strong> &nbsp; {store.custom_domain} &nbsp; &rarr; &nbsp; {store.slug}.cartz.cc
            </div>
            <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
              {t(
                `Make sure your DNS has a CNAME record pointing to ${store.slug}.cartz.cc`,
                `تأكد من إضافة سجل CNAME في DNS يشير إلى ${store.slug}.cartz.cc`
              )}
            </p>
          </div>
        </div>
      )}

      {/* Domain Setup */}
      {!store?.custom_domain && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2>{t('Custom Domain', 'النطاق المخصص')}</h2>
          </div>
          <div className="card-body">
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid var(--gray-100)' }}>
              <button
                onClick={() => setTab('own')}
                style={{
                  padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 14,
                  borderBottom: tab === 'own' ? '2px solid var(--primary)' : '2px solid transparent',
                  color: tab === 'own' ? 'var(--primary)' : 'var(--gray-400)',
                  marginBottom: -2,
                }}>
                {t('I have a domain', 'لدي نطاق')}
              </button>
              {isPaidPlan && (
                <button
                  onClick={() => setTab('register')}
                  style={{
                    padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: 14,
                    borderBottom: tab === 'register' ? '2px solid var(--primary)' : '2px solid transparent',
                    color: tab === 'register' ? 'var(--primary)' : 'var(--gray-400)',
                    marginBottom: -2,
                  }}>
                  {t('Register new domain', 'تسجيل نطاق جديد')}
                  {pricingInfo?.eligible_for_free && (
                    <span style={{ marginInlineStart: 6, fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#dcfce7', color: '#16a34a', fontWeight: 700 }}>{t('FREE', 'مجاني')}</span>
                  )}
                </button>
              )}
            </div>

            {/* Tab: Own Domain */}
            {tab === 'own' && (
              <div>
                <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 12 }}>
                  {t(
                    'If you already have a registered domain, enter it below. You will need to add a CNAME DNS record pointing to your store subdomain.',
                    'إذا كان لديك نطاق مسجل مسبقاً، أدخله أدناه. ستحتاج لإضافة سجل CNAME في DNS يشير إلى رابط متجرك الفرعي.'
                  )}
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input
                    className="form-input"
                    value={ownDomain}
                    onChange={(e) => setOwnDomain(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveOwnDomain()}
                    placeholder="example.com"
                    dir="ltr"
                    style={{ flex: 1, textAlign: 'left' }}
                  />
                  <button className="btn btn-primary" onClick={saveOwnDomain} disabled={savingOwn || !ownDomain.trim()}>
                    {savingOwn ? '...' : t('Save', 'حفظ')}
                  </button>
                </div>
                <div style={{ padding: 16, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <strong style={{ fontSize: 13, color: '#1e40af' }}>{t('DNS Setup Instructions', 'تعليمات إعداد DNS')}</strong>
                  <ol style={{ fontSize: 13, color: '#374151', margin: '8px 0 0', paddingInlineStart: 20, lineHeight: 1.8 }}>
                    <li>{t('Go to your domain registrar (GoDaddy, Namecheap, etc.)', 'اذهب إلى مسجل النطاق الخاص بك (GoDaddy، Namecheap، إلخ.)')}</li>
                    <li>{t('Open DNS settings for your domain', 'افتح إعدادات DNS لنطاقك')}</li>
                    <li>{t('Add a CNAME record:', 'أضف سجل CNAME:')}</li>
                  </ol>
                  <div style={{ marginTop: 8, padding: '10px 14px', background: 'white', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', direction: 'ltr', textAlign: 'left' }}>
                    <strong>Type:</strong> CNAME<br />
                    <strong>Name:</strong> @ {t('(or your domain)', '(أو النطاق الخاص بك)')}<br />
                    <strong>Value:</strong> {store?.slug}.cartz.cc
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Register New Domain */}
            {tab === 'register' && isPaidPlan && (
              <div>
                {pricingInfo?.eligible_for_free && (
                  <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, marginBottom: 16 }}>
                    <strong style={{ color: '#16a34a' }}>{t('Free domain included with your plan!', 'نطاق مجاني مع خطتك!')}</strong>
                    <p style={{ fontSize: 13, color: '#166534', margin: '4px 0 0' }}>
                      {t(
                        'Domains marked FREE are covered. Others show the extra charge.',
                        'النطاقات المحددة بـ "مجاني" أدناه مغطاة. الباقي يظهر التكلفة الإضافية.'
                      )}
                    </p>
                  </div>
                )}
                {pricingInfo?.free_domain_used && (
                  <div style={{ padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 16 }}>
                    <strong style={{ color: '#1e40af' }}>{t('Free domain already used:', 'تم استخدام النطاق المجاني:')}</strong>
                    <span style={{ marginInlineStart: 8, fontFamily: 'monospace', fontWeight: 700 }}>{pricingInfo.free_domain_used}</span>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <input
                    className="form-input"
                    value={searchDomain}
                    onChange={(e) => setSearchDomain(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && checkDomain()}
                    placeholder={t('Enter domain name, e.g. mystore', 'أدخل اسم النطاق، مثال: mystore')}
                    dir="ltr"
                    style={{ flex: 1, textAlign: 'left' }}
                  />
                  <button className="btn btn-primary" onClick={checkDomain} disabled={checking || !searchDomain.trim()}>
                    {checking ? t('Searching...', 'جارٍ البحث...') : t('Search', 'بحث')}
                  </button>
                </div>

                {checking && (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--gray-400)' }}>
                    <div style={{ fontSize: 14 }}>{t('Checking domain availability...', 'جارٍ التحقق من توفر النطاق...')}</div>
                  </div>
                )}

                {!checking && results.length > 0 && (
                  <div dir="ltr" style={{ display: 'flex', flexDirection: 'column', gap: 6, direction: 'ltr' }}>
                    {results.map((r: any) => {
                      const isFree = pricingInfo?.eligible_for_free && r.free_eligible;
                      return (
                        <div key={r.domain} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 14px', borderRadius: 8,
                          border: '1px solid',
                          borderColor: r.available ? (isFree ? '#86efac' : '#dcfce7') : 'var(--gray-100)',
                          background: r.available ? (isFree ? '#f0fdf4' : '#fafafa') : 'var(--gray-50)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 14, fontFamily: 'monospace', direction: 'ltr' }}>{r.domain}</strong>
                            <span style={{
                              fontSize: 11, padding: '2px 8px', borderRadius: 50, fontWeight: 600,
                              background: r.available ? '#dcfce7' : '#fee2e2',
                              color: r.available ? '#16a34a' : '#dc2626',
                            }}>
                              {r.available ? t('Available', 'متاح') : t('Taken', 'محجوز')}
                            </span>
                            {r.available && r.cost_usd !== undefined && (
                              isFree ? (
                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#16a34a', color: 'white', fontWeight: 700 }}>
                                  {t('FREE', 'مجاني')}
                                </span>
                              ) : r.extra_charge > 0 ? (
                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
                                  +{(r.extra_charge * 0.307).toFixed(3)} {t('KD', 'د.ك')}
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{(r.cost_usd * 0.307).toFixed(3)} {t('KD', 'د.ك')}</span>
                              )
                            )}
                          </div>
                          {r.available && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => registerDomain(r.domain, !!isFree)}
                              disabled={registering === r.domain || (!isFree && (r.extra_charge || 0) > 0)}
                              title={!isFree && (r.extra_charge || 0) > 0 ? t('Extra payment required', 'يتطلب دفع إضافي') : ''}
                            >
                              {registering === r.domain ? '...' : (isFree ? t('Register Free', 'تسجيل مجاني') : t('Register', 'تسجيل'))}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {registered && (
                  <div style={{ marginTop: 16, padding: 16, background: '#f0fdf4', border: '2px solid #16a34a', borderRadius: 8 }}>
                    <h4 style={{ margin: '0 0 8px', color: '#16a34a' }}>{t('Domain Registered!', 'تم تسجيل النطاق!')}</h4>
                    <p style={{ fontSize: 13, margin: '0 0 8px' }}>
                      <strong>{registered.domain}</strong> {t('has been registered and configured automatically.', 'تم تسجيله وإعداده تلقائياً.')}
                    </p>
                    {registered.nameservers?.length > 0 && (
                      <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {t('Nameservers', 'خوادم الأسماء')}: <code>{registered.nameservers.join(', ')}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === 'register' && !isPaidPlan && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <p style={{ color: 'var(--gray-400)', fontSize: 14 }}>
                  {t(
                    'Domain registration is available with Business and Enterprise plans.',
                    'تسجيل النطاق متاح مع خطة الأعمال والمؤسسات.'
                  )}
                </p>
                <a href="/dashboard/subscription" className="btn btn-primary" style={{ marginTop: 12 }}>
                  {t('Upgrade Plan', 'ترقية الخطة')}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
